/**
 * @file ClaudeSpendPanel.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-14
 * @modified 2026-09-14
 *
 * The Spend view of the logger widget: what the Claude API has cost, and
 * whether it is currently working.
 *
 * Reads GET /api/system-logs/ai-spend, which sums audit.ai_usage — a row per
 * API call written by claudeUsageService. The figures are estimates from token
 * counts at list prices; the Anthropic Console bill is the authority, and the
 * footnote says so rather than letting a dashboard number pass for an invoice.
 *
 * The status line leads because it answers the question that prompted this
 * view: the API account ran out of credit and nothing in the system said so.
 * An account-level failure newer than the last successful call shows as
 * PAUSED until a call succeeds again.
 *
 * Shares the alerting panel's terminal palette and toolbar/pill/empty-state
 * rules by carrying its root classes (.system-log-alerts.variant-terminal);
 * only what is particular to spend lives in ClaudeSpendPanel.css.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../config';
import LoadingSpinner from './LoadingSpinner';
import { formatCurrency } from '../utils/currencyFormatter';
import './SystemLogAlerts.css';
import './ClaudeSpendPanel.css';

const REFRESH_MS = 120000;

const ERROR_KIND_LABELS = {
  billing: 'billing problem',
  credit: 'out of credit',
  spend_limit: 'spend limit reached',
  spend_cap: 'monthly tier cap reached',
  auth: 'API key rejected',
  permission: 'API key not permitted'
};

/** Anthropic bills in dollars, so these are dollars — not the app's usual £. */
function usd(value) {
  const n = Number(value || 0);
  if (n > 0 && n < 0.01) return '<$0.01';
  return `$${formatCurrency(n, { showSymbol: false })}`;
}

function tokens(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${Math.round(v / 1000)}k`;
  return String(v);
}

function relative(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function ClaudeSpendPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSpend = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/system-logs/ai-spend`, { credentials: 'include' });
      if (res.status === 403) { setError('Forbidden — admin only.'); return; }
      if (!res.ok) { setError(`HTTP ${res.status}`); return; }
      setData(await res.json());
      setError(null);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSpend(); }, [fetchSpend]);

  useEffect(() => {
    const handle = setInterval(fetchSpend, REFRESH_MS);
    return () => clearInterval(handle);
  }, [fetchSpend]);

  const maxDaily = useMemo(
    () => Math.max(0, ...(data?.daily || []).map(d => d.cost)),
    [data]
  );

  const account = data?.account;
  const paused = Boolean(account?.paused);

  return (
    <div className="system-log-alerts variant-terminal claude-spend">
      <div className="sla-toolbar">
        <div className="sla-tally">
          {data && (
            paused ? (
              <span className="sla-pill sla-pill-err" title={account.lastError?.message || ''}>
                <span className="material-symbols-rounded">pause_circle</span>
                PAUSED · {ERROR_KIND_LABELS[account.lastError?.kind] || account.lastError?.kind}
              </span>
            ) : (
              <span className="sla-pill csp-pill-ok">
                <span className="material-symbols-rounded">check_circle</span>
                API OK
              </span>
            )
          )}
          <span className="sla-tally-text">
            {data ? `last successful call ${relative(account?.lastOkAt)}` : 'claude api spend'}
          </span>
        </div>
        <div className="sla-toolbar-right">
          <button type="button" className="btn-icon sla-refresh" onClick={fetchSpend} title="Refresh">
            <span className="material-symbols-rounded">refresh</span>
          </button>
        </div>
      </div>

      <div className="sla-body">
        {loading && !data ? (
          <div className="sla-empty"><LoadingSpinner /></div>
        ) : error ? (
          <div className="sla-empty sla-error">
            <span className="material-symbols-rounded">error</span> {error}
          </div>
        ) : (
          <div className="csp-content">
            {paused && (
              <div className="csp-paused">
                <strong>
                  Claude API calls are being refused — {ERROR_KIND_LABELS[account.lastError.kind] || account.lastError.kind}.
                </strong>
                <p>
                  Every AI feature is paused. Top up or raise the limit in the Claude Console
                  under Settings → Billing. This clears on the next successful call.
                </p>
                <p className="csp-muted">
                  {shortDate(account.lastError.at)} {relative(account.lastError.at)} · {account.lastError.feature}
                  {account.lastError.message ? ` · “${account.lastError.message}”` : ''}
                </p>
              </div>
            )}

            <div className="csp-stats">
              <div className="csp-stat">
                <span className="csp-stat-label">This month</span>
                <span className="csp-stat-value">{usd(data.thisMonth.cost)}</span>
                <span className="csp-stat-sub">
                  {data.thisMonth.calls} call{data.thisMonth.calls === 1 ? '' : 's'}
                  {data.thisMonth.errors > 0 && (
                    <span className="csp-err-text"> · {data.thisMonth.errors} failed</span>
                  )}
                </span>
              </div>
              <div className="csp-stat">
                <span className="csp-stat-label">Last month</span>
                <span className="csp-stat-value">{usd(data.lastMonth.cost)}</span>
                <span className="csp-stat-sub">
                  {data.lastMonth.calls} call{data.lastMonth.calls === 1 ? '' : 's'}
                </span>
              </div>
              <div className="csp-stat">
                <span className="csp-stat-label">Last {data.windowDays} days</span>
                <span className="csp-stat-value">{usd(data.window.cost)}</span>
                <span className="csp-stat-sub">peak day {usd(maxDaily)}</span>
              </div>
            </div>

            <div className="csp-section-title">Daily spend</div>
            <div className="csp-chart" role="img" aria-label={`Daily Claude API spend over the last ${data.windowDays} days`}>
              {data.daily.map((d) => {
                const pct = maxDaily > 0 ? Math.max(d.cost > 0 ? 4 : 0, (d.cost / maxDaily) * 100) : 0;
                return (
                  <div
                    key={d.day}
                    className={`csp-bar-slot${d.errors > 0 ? ' has-errors' : ''}`}
                    title={`${shortDate(d.day)} · ${usd(d.cost)} · ${d.calls} call${d.calls === 1 ? '' : 's'}${d.errors ? ` · ${d.errors} failed` : ''}`}
                  >
                    <div className="csp-bar" style={{ height: `${pct}%` }} />
                  </div>
                );
              })}
            </div>
            <div className="csp-chart-axis">
              <span>{shortDate(data.daily[0]?.day)}</span>
              <span>{shortDate(data.daily[data.daily.length - 1]?.day)}</span>
            </div>

            <div className="csp-section-title">By feature · last {data.windowDays} days</div>
            {data.byFeature.length === 0 ? (
              <div className="csp-muted csp-none">No calls in this window.</div>
            ) : (
              <div className="csp-table-scroll">
              <table className="csp-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className="num">Calls</th>
                    <th className="num">Tokens in / out</th>
                    <th className="num">Cost</th>
                    <th className="num">Last used</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byFeature.map((f) => (
                    <tr key={f.feature}>
                      <td>{f.label}</td>
                      <td className="num">
                        {f.calls}
                        {f.errors > 0 && <span className="csp-err-text"> +{f.errors}✕</span>}
                      </td>
                      <td className="num">{tokens(f.inputTokens)} / {tokens(f.outputTokens)}</td>
                      <td className="num">{usd(f.cost)}</td>
                      <td className="num csp-muted">{relative(f.lastUsedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}

            <p className="csp-footnote">
              Estimated from token counts at list prices — the Claude Console bill is authoritative.
              Days and months are UK dates.
              {' '}Bill reading, blend briefs and Ask the Data are recorded
              {data.firstLiveAt ? ` from ${shortDate(data.firstLiveAt)}` : ' from their next call'};
              earlier history covers screenshot and price-list reads only.
              {data.unpricedCalls > 0 && ` ${data.unpricedCalls} call(s) used a model with no price on file and count as $0.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClaudeSpendPanel;
